import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
} from "lucide-react";
import {
  Button,
  EmptyState,
  LoadingState,
  SectionCard,
} from "../../components/ui";
import { pontoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { Tabela, Td, Th } from "./TabelaCompacta";
import { dataCompleta } from "./pontoUtils";

/**
 * Dias sem expediente do ponto. A fonte é o Cadastro de impedimentos (o mesmo que bloqueia o
 * caixa diário): esta aba só mostra o que vale para o ponto e importa os feriados do ano.
 */
const DiasFechadosTab = () => {
  const { showError, showSuccess } = useNotification();
  const [ano, setAno] = useState(new Date().getFullYear());
  const [dias, setDias] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [importando, setImportando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDias(await pontoService.listarDiasFechados(ano));
    } catch (erro) {
      showError(erro.message);
    } finally {
      setCarregando(false);
    }
  }, [ano]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregar();
  }, [carregar]);

  const importar = async () => {
    setImportando(true);
    try {
      const resultado = await pontoService.importarFeriados(ano);
      showSuccess(
        resultado.incluidos
          ? `${resultado.incluidos} feriado(s) de ${ano} gravado(s) no Cadastro de impedimentos.`
          : `Os feriados de ${ano} já estavam cadastrados.`,
      );
      await carregar();
    } catch (erro) {
      showError(erro.message);
    } finally {
      setImportando(false);
    }
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
      <SectionCard bodyClassName="p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              icon={ChevronLeft}
              aria-label="Ano anterior"
              onClick={() => setAno(ano - 1)}
            />
            <span className="min-w-[60px] text-center font-semibold">
              {ano}
            </span>
            <Button
              variant="ghost"
              size="sm"
              icon={ChevronRight}
              aria-label="Próximo ano"
              onClick={() => setAno(ano + 1)}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={Download}
            loading={importando}
            onClick={importar}
          >
            Importar feriados de {ano}
          </Button>
        </div>
        {carregando ? (
          <LoadingState />
        ) : dias.length === 0 ? (
          <EmptyState
            title={`Nenhum dia fechado em ${ano}.`}
            description="Use “Importar feriados” ou cadastre em Cadastro de impedimentos."
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th>Data</Th>
                <Th>Dia</Th>
                <Th>Motivo</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {dias.map((dia) => (
                <tr key={dia.data}>
                  <Td className="tabular-nums">{dataCompleta(dia.data)}</Td>
                  <Td>{dia.diaSemana}</Td>
                  <Td>{dia.descricao}</Td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}
      </SectionCard>

      <SectionCard>
        <div className="space-y-3 text-sm text-gray-700">
          <h3 className="font-semibold text-gray-900">
            De onde vêm esses dias
          </h3>
          <p>
            Do <strong>Cadastro de impedimentos</strong>, o mesmo que bloqueia o
            caixa diário. Um cadastro só vale para o caixa e para o ponto.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-gray-600">
            <li>
              Sem usuário: dia fechado para <strong>todas</strong> (feriado,
              clínica fechada).
            </li>
            <li>
              Com usuário: não conta aqui. Folga de uma colaboradora é
              cadastrada em <strong>Afastamentos</strong> (tipo Folga).
            </li>
            <li>“Datas fixas”: repete todo ano.</li>
          </ul>
          <p className="text-gray-600">
            Nesses dias ninguém leva falta, e quem trabalhar recebe hora extra
            100%.
          </p>
          <Link
            to="/impedimento"
            className="inline-flex items-center gap-1 font-medium text-blue-700 hover:underline"
          >
            Abrir Cadastro de impedimentos <ExternalLink size={14} />
          </Link>
          <p className="border-t border-gray-100 pt-3 text-xs text-gray-500">
            Os feriados obrigatórios (nacionais, Paixão de Cristo e aniversário
            de Itajaí) são gravados <strong>automaticamente</strong> no Cadastro
            de impedimentos, do ano atual e do próximo, uma vez por ano. Se você
            excluir algum, ele não volta sozinho. O botão “Importar” só é
            necessário para refazer um ano. Facultativos (Carnaval, Corpus
            Christi) não entram: cadastre se a clínica fechar.
          </p>
        </div>
      </SectionCard>
    </div>
  );
};

export default DiasFechadosTab;
